import { TranslateFileTablet } from "@models/company"
import { useContext } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { UserContext, useLanguages } from "src/providers"
import { useResponsive } from "../../utils/useResponsive"
import { useLocalStorage } from "../../utils/useLocalStorage"
import { GENRES, Genre } from "src/models/common"
import { Language } from "../../utils/useLanguageList"

export const useTranslateFile = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { isMobile } = useResponsive()
  const { tablets, companyPortalProperties } = useContext(UserContext)
  const {
    allUserLanguages,
    companyAllowedLanguages,
    aiFileTranslatorDefaultLangFrom,
    aiFileTranslatorDefaultLangTo,
    aiFileTranslatorOutsourcingDefaultLangFrom,
    aiFileTranslatorOutsourcingDefaultLangTo
  } = useLanguages()
  const tablet = tablets.find((t) => t.id == id) as TranslateFileTablet
  if (!tablet) {
    navigate("")
    throw Error("Invalid tablet")
  }

  const withInsourcingTrusminer =
    tablet.stages.filter((s) => s.stage === "TRUST_MINING" && s.executorSelection === "insourcing").length > 0
  const withInsourcingEnhancer =
    tablet.stages.filter((s) => s.stage === "ENHANCEMENT" && s.executorSelection === "insourcing").length > 0
  const showInstructionsButton = tablet.stages.filter((s) => s.stage === "ENHANCEMENT").length > 0 && !isMobile
  const workflowWithAnonymizatioin = tablet.stages.filter((s) => s.stage === "ANONYMIZATION").length > 0
  const isOutsourcing = ["FIXED", "FIXED_ASSET_BASED"].includes(tablet.pricingType)
  const allowedLanguages = isOutsourcing ? companyAllowedLanguages : allUserLanguages
  const taxonomies = companyPortalProperties.taxonomy.split(", ")

  const [langFrom, setLangFrom] = useLocalStorage<Language>(
    `${tablet.id}.initialLangFrom`,
    isOutsourcing ? aiFileTranslatorOutsourcingDefaultLangFrom : aiFileTranslatorDefaultLangFrom,
    (value: Language) => allowedLanguages.some((l) => l.languageId === value.languageId),
    [allowedLanguages, aiFileTranslatorDefaultLangFrom, isOutsourcing]
  )
  const [langsTo, setLangsTo] = useLocalStorage<Language[]>(
    `${tablet.id}.initialLangsTo`,
    [isOutsourcing ? aiFileTranslatorOutsourcingDefaultLangTo : aiFileTranslatorDefaultLangTo],
    (value: Language[]) => value.every((v) => allowedLanguages.some((l) => l.languageId === v.languageId)),
    [allowedLanguages, aiFileTranslatorDefaultLangTo, isOutsourcing]
  )
  const [genre, setGenre] = useLocalStorage<Genre>(
    "initialGenre",
    GENRES.find((g) => g.id === companyPortalProperties.genre) || GENRES[0],
    (value: Genre) => GENRES.some((g) => g.id === value.id)
  )

  return {
    tablet,
    withInsourcingTrusminer,
    withInsourcingEnhancer,
    showInstructionsButton,
    workflowWithAnonymizatioin,
    isOutsourcing,
    allowedLanguages,
    taxonomies,
    langFrom,
    setLangFrom,
    langsTo,
    setLangsTo,
    genre,
    setGenre
  }
}
